import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IRegulation } from '../contracts/amendment.contract';
import { ICriteria } from '../contracts/criteria.contract';
import { RegulationService } from '../services';
import { RootState } from './store.redux';

interface IInitialState {
    ByCriteria: {
        Regulations: IRegulation[] | undefined;
        IsLoading: boolean;
        ErrorMessage: string | undefined;
    };
    ByVessel: {
        Regulations: IRegulation[] | undefined;
        Count: number;
        Total: number;
        Filters: {
            ClassNumbers: string[];
        };
        IsLoading: boolean;
        ErrorMessage: string | undefined;
    };
    AxiosController: AbortController;
}

const initialState: IInitialState = {
    ByCriteria: {
        Regulations: undefined,
        IsLoading: false,
        ErrorMessage: undefined
    },
    ByVessel: {
        Regulations: undefined,
        Count: 0,
        Total: 0,
        Filters: {
            ClassNumbers: []
        },
        IsLoading: false,
        ErrorMessage: undefined
    },
    AxiosController: new AbortController()
};

const Fetch = {
    GetByCriteria: createAsyncThunk('regulation/GetByCriteria', async (criteria: ICriteria, { rejectWithValue, getState }) => {
        try {
            const rootState = getState() as RootState;
            const res = await RegulationService.getByCriteria(criteria, rootState.Regulation.AxiosController);
            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    }),
    GetByVesselClassNumbers: createAsyncThunk('regulation/GetByVesselClassNumbers', async (classNumbers: string[], { rejectWithValue, getState }) => {
        try {
            const rootState = getState() as RootState;
            const res = await RegulationService.getByVesselClassNumbers(classNumbers, rootState.Regulation.AxiosController);
            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    })
};

const slice = createSlice({
    name: 'regulation',
    initialState: initialState,
    reducers: {
        ByVesselAddFilterClassNumber: (state, action: PayloadAction<string>) => {
            const { payload: classNumber } = action;
            const { ClassNumbers } = state.ByVessel.Filters;

            if (!ClassNumbers.includes(classNumber)) {
                ClassNumbers.push(classNumber);
            }
        },
        ByVesselRemoveFilterClassNumber: (state, action: PayloadAction<string>) => {
            const { payload: classNumber } = action;
            const { ClassNumbers } = state.ByVessel.Filters;

            if (ClassNumbers.includes(classNumber)) {
                const index = ClassNumbers.indexOf(classNumber);
                ClassNumbers.splice(index, 1);
            }
        },
        ClearByVesselRegulations: (state) => {
            state.ByVessel.Regulations = undefined;
        },
        CancelApiRequests: (state) => {
            state.AxiosController.abort();
            state.AxiosController = new AbortController();
        }
    },
    extraReducers: (builder) => {
        // GetByCriteria
        builder.addCase(Fetch.GetByCriteria.pending, (state, action) => {
            state.ByCriteria.IsLoading = true;
            state.ByCriteria.ErrorMessage = undefined;
        });
        builder.addCase(Fetch.GetByCriteria.fulfilled, (state, action: PayloadAction<IRegulation[] | undefined>) => {
            state.ByCriteria.Regulations = action.payload;
            state.ByCriteria.IsLoading = false;
            state.ByCriteria.ErrorMessage = undefined;
        });
        builder.addCase(Fetch.GetByCriteria.rejected, (state, action) => {
            state.ByCriteria.Regulations = undefined;
            state.ByCriteria.IsLoading = false;
            state.ByCriteria.ErrorMessage = action.payload?.toString();
        });
        // GetByVesselClassNumbers
        builder.addCase(Fetch.GetByVesselClassNumbers.pending, (state, action) => {
            state.ByVessel.IsLoading = true;
            state.ByVessel.Count++;
            state.ByVessel.ErrorMessage = undefined;
        });
        builder.addCase(Fetch.GetByVesselClassNumbers.fulfilled, (state, action: PayloadAction<IRegulation[] | undefined>) => {
            if (!action.payload) return;

            // Merge new regulations with existing regulations and remove duplicates based on a unique property (e.g., id)
            const existingRegulations = state.ByVessel.Regulations ?? [];
            const newRegulations = action.payload ?? [];
            const combinedRegulations = [...existingRegulations, ...newRegulations];

            const uniqueRegulationsMap = new Map<string, IRegulation>();
            combinedRegulations.forEach((regulation) => {
                uniqueRegulationsMap.set(`${regulation.RegulationName}_${regulation.RegulationDocName}`, regulation); // RegualtionName && RegulationDocName the unique property
            });

            state.ByVessel.Regulations = Array.from(uniqueRegulationsMap.values());
            state.ByVessel.IsLoading = false;
            state.ByVessel.ErrorMessage = undefined;
        });
        builder.addCase(Fetch.GetByVesselClassNumbers.rejected, (state, action) => {
            state.ByVessel.IsLoading = false;
            state.ByVessel.ErrorMessage = action.payload?.toString();
        });
    }
});

export const RegulationFetch = Fetch;
export const RegulationAction = slice.actions;
export const RegulationReducer = slice.reducer;
